export interface InstalledIntegration {
  id: string;
  userId: string;
  integration: InstalledIntegrationType;
  externalId: string;
  // Upstream login identifier for this credential. Today this is the email
  // for Gmail / Outlook; typed generically so future integrations whose
  // login isn't email-shaped can populate it without a type change.
  username?: string | null;
  status?: InstalledIntegrationStatus;
  createdAt?: string | number;
}

export enum InstalledIntegrationStatus {
  ACTIVE = "ACTIVE",
  NEEDS_RECONNECT = "NEEDS_RECONNECT",
}

export enum InstalledIntegrationType {
  SALESFORCE = "SALESFORCE",
  WEALTHBOX = "WEALTHBOX",
  REDTAIL = "REDTAIL",
  ZOOM = "ZOOM",
  GOOGLECALENDAR = "GOOGLECALENDAR",
  MICROSOFTCALENDAR = "MICROSOFTCALENDAR",
  PARAGON_SALESFORCE = "PARAGON_SALESFORCE",
  PARAGON_XLR8 = "PARAGON_XLR8",
  PARAGON_PRACTIFI = "PARAGON_PRACTIFI",
  PARAGON_SS_C_BLACKDIAMONDELEMENTS = "PARAGON_SS_C_BLACKDIAMONDELEMENTS",
  PARAGON_FSC = "PARAGON_FSC",
  PARAGON_MICROSOFTDYNAMICS = "PARAGON_MICROSOFTDYNAMICS",
  EMONEY = "EMONEY",
  PARAGON_ZOHO = "PARAGON_ZOHO",
  PARAGON_CLOVEN = "PARAGON_CLOVEN",
  PARAGON_MAXIMIZER = "PARAGON_MAXIMIZER",
  PARAGON_EQUISOFT = "PARAGON_EQUISOFT",
  PARAGON_GOOGLE = "PARAGON_GOOGLE",
  PARAGON_MICROSOFT = "PARAGON_MICROSOFT",
  CONQUEST = "CONQUEST",
  LAYLAH = "LAYLAH",
}

/** The mail-provider vendor: Microsoft (Outlook) or Google (Gmail). */
export type MailProvider = "google" | "microsoft";

/** A single mail-capable installed integration surfaced to the UI. */
export interface MailAccount {
  integrationId: string;
  email: string;
  supportsCalendar: boolean;
  supportsEmail: boolean;
}

/** The winning mail provider + account pair chosen for the current user. */
export interface MailProviderInfo {
  provider: MailProvider;
  account: MailAccount;
}

/** Integration types that grant access to a Microsoft (Outlook) mailbox. */
export const MICROSOFT_MAIL_INTEGRATION_TYPES: InstalledIntegrationType[] = [
  InstalledIntegrationType.MICROSOFTCALENDAR,
  InstalledIntegrationType.PARAGON_MICROSOFT,
];

/** Integration types that grant access to a Google (Gmail) mailbox. */
export const GOOGLE_MAIL_INTEGRATION_TYPES: InstalledIntegrationType[] = [
  InstalledIntegrationType.GOOGLECALENDAR,
  InstalledIntegrationType.PARAGON_GOOGLE,
];

/**
 * Integration types that can read and send email. The calendar integrations
 * share the same OAuth vendors but carry calendar-only scopes, so they are
 * deliberately absent.
 */
export const EMAIL_INTEGRATION_TYPES: InstalledIntegrationType[] = [
  InstalledIntegrationType.PARAGON_GOOGLE,
  InstalledIntegrationType.PARAGON_MICROSOFT,
];

/** Integration types that are CRMs — mirrors the `CrmType` enum. */
export const CRM_INTEGRATION_TYPES: InstalledIntegrationType[] = [
  InstalledIntegrationType.SALESFORCE,
  InstalledIntegrationType.WEALTHBOX,
  InstalledIntegrationType.REDTAIL,
  InstalledIntegrationType.LAYLAH,
  InstalledIntegrationType.PARAGON_SALESFORCE,
  InstalledIntegrationType.PARAGON_XLR8,
  InstalledIntegrationType.PARAGON_PRACTIFI,
  InstalledIntegrationType.PARAGON_SS_C_BLACKDIAMONDELEMENTS,
  InstalledIntegrationType.PARAGON_FSC,
  InstalledIntegrationType.PARAGON_MICROSOFTDYNAMICS,
  InstalledIntegrationType.PARAGON_ZOHO,
  InstalledIntegrationType.PARAGON_CLOVEN,
  InstalledIntegrationType.PARAGON_MAXIMIZER,
  InstalledIntegrationType.PARAGON_EQUISOFT,
];
